import { AccessControlState } from "./types";
import { ActionTree } from "vuex";
import { RootState } from "@/store/types";
import { graph } from "@/store/api";
import { gql } from "@apollo/client/core";
import _ from "lodash";
import { isShowAdvanced } from "@/store/getters";

/**
 * Actions for the Access Control module.
 * Handles async operations for user permissions and Auth0 management.
 */
const actions: ActionTree<AccessControlState, RootState> = {
  /**
   * Fetches users from Auth0 with pagination and search.
   * Supports different query contexts (livechat vs standard).
   *
   * @param {ActionContext} context - Vuex action context
   * @param {Object} payload - Query parameters
   * @param {number} payload.page - Page number for pagination
   * @param {number} payload.size - Page size
   * @param {string} payload.search - Search query string
   * @param {number} payload.order - Sort order
   * @param {boolean} payload.fromLivechat - Whether called from livechat context
   * @returns {Promise<void>}
   */
  FETCH_AUTH_USERS: ({ state, dispatch, commit, rootState }, payload = {}) => {
    const variables = {};
    const { page, size, search, order } = payload;
    Object.assign(variables, { page, size, search, order });

    // TODO: Update the existing gql endpoint to eliminate the need for checking operationName for authentication purposes
    const query = payload?.fromLivechat
      ? gql`
          query users(
            $includeKeyReply: Boolean
            $page: Int
            $size: Int
            $search: String
            $order: Int
          ) {
            accesscontrolAPI {
              users(
                includeKeyReply: $includeKeyReply
                page: $page
                size: $size
                search: $search
                order: $order
              )
            }
          }
        `
      : gql`
          query ($includeKeyReply: Boolean, $page: Int, $size: Int, $search: String, $order: Int) {
            accesscontrolAPI {
              users(
                includeKeyReply: $includeKeyReply
                page: $page
                size: $size
                search: $search
                order: $order
              )
            }
          }
        `;

    return graph
      .query({
        query,
        variables: {
          includeKeyReply: isShowAdvanced(rootState),
          ...variables,
        },
        fetchPolicy: "network-only",
      })
      .then((result) => {
        commit("SET_AUTH_USERS", _.get(result, "data.accesscontrolAPI.users.users"));
        commit("SET_AUTH_USER_TOTAL", _.get(result, "data.accesscontrolAPI.users.total"));
      });
  },
  /**
   * Saves bulk user updates to Auth0.
   *
   * @param {ActionContext} context - Vuex action context
   * @param {Object} payload - Action payload
   * @param {Array} payload.users - Array of user objects to update
   * @returns {Promise<Object>} Mutation result
   */
  SAVE_USERS: ({ state, commit }, { users }) => {
    return graph.mutate({
      mutation: gql`
        mutation ($users: [JSON]) {
          accesscontrolAPI {
            updateUsers(users: $users)
          }
        }
      `,
      variables: {
        users,
      },
    });
  },

  /**
   * Adds a new user to Auth0 and updates state.
   *
   * @param {ActionContext} context - Vuex action context
   * @param {Object} user - User object to create
   * @returns {Promise<boolean>} True if successful
   */
  ADD_USER: async ({ state, commit }, user) => {
    return graph
      .mutate({
        mutation: gql`
          mutation ($user: JSON) {
            accesscontrolAPI {
              addUser(user: $user)
            }
          }
        `,
        variables: {
          user,
        },
      })
      .then((res: any) => {
        const newUser = _.get(res, "data.accesscontrolAPI.addUser");
        if (newUser) {
          commit("ADD_AUTH_USER", newUser);
          return true;
        }
      })
      .catch((err) => {});
  },

  /**
   * Deletes a user from Auth0 and removes from state.
   *
   * @param {ActionContext} context - Vuex action context
   * @param {Object} payload - Action payload
   * @param {string} payload.email - User email to delete
   * @param {string} payload.userId - User ID to delete
   * @returns {Promise<boolean>} True if successful
   */
  DELETE_USER: async ({ state, commit }, { email, userId }) => {
    return graph
      .mutate({
        mutation: gql`
          mutation ($email: String!, $userId: String!) {
            accesscontrolAPI {
              deleteUser(email: $email, userId: $userId)
            }
          }
        `,
        variables: {
          email,
          userId,
        },
      })
      .then((res: any) => {
        const userDeleted = _.get(res, "data.accesscontrolAPI.deleteUser");
        if (userDeleted) {
          commit("DELETE_AUTH_USER", email);
          return true;
        }

        return false;
      })
      .catch((err) => {});
  },
};

export default actions;
